import { useEffect, useRef, useCallback, useState } from "react";
import { Canvas } from "fabric";
import { useData } from "../hooks/useData";
import { FontNames } from "./FontNames";
import useZoom from "../hooks/useZoom";
import useCanvasHistory from "../hooks/useCanvasHistory";
import useCanvasResize from "../hooks/useCanvasResize";
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import PanToolIcon from "@mui/icons-material/PanTool";
import Ruler from "./Ruler";

// Mapping of font names to their file paths (aligned with FontsStyleSheet.css)
const FONT_PATHS = {
  Ubuntu: "/fonts/Ubuntu-Regular.ttf",
  Pacifico: "/fonts/Pacifico-Regular.ttf",
  AbrilFatface: "/fonts/AbrilFatface-Regular.ttf",
  Acme: "/fonts/Acme-Regular.ttf",
  "Alfa Slab": "/fonts/AlfaSlabOne-Regular.ttf",
  Americana: "/fonts/GT-Americana_Bold_v2.ttf",
  "Antique Olive": "/fonts/GT-AntiqueOliveMedium.ttf",
  Anton: "/fonts/Anton-Regular.ttf",
  Antonio: "/fonts/Antonio-Regular.ttf",
  Architectural: "/fonts/GT-Architectural_v2.ttf",
  "Archivo Narrow": "/fonts/ArchivoNarrow-Medium.ttf",
  Arial: "/fonts/GT-Arial_v2.ttf",
  "Arial Bold": "/fonts/GT-Arial_Bold_v2.ttf",
  Audiowide: "/fonts/Audiowide-Regular.ttf",
  "Avant Extra Bold": "/fonts/GT-Avant_Extra_Bold.ttf",
  "Avant Garde Medium": "/fonts/GT-Avant_Garde_Medium_v2.ttf",
  Balthazer: "/fonts/Balthazar-Regular.ttf",
  Bauhaus: "/fonts/GT-Bauhaus.ttf",
  Bangers: "/fonts/Bangers-Regular.ttf",
  "Barlow Condensed": "/fonts/BarlowCondensed-Regular.ttf",
  "Bebas Neue": "/fonts/BebasNeue-Regular.ttf",
  Benguiat: "/fonts/GT-Benguiat_v2.ttf",
  "Berkshire Swash": "/fonts/BerkshireSwash-Regular.ttf",
  "Bodoni Italic": "/fonts/LibreBodoni-SemiBoldItalic.ttf",
  "Brush Script": "/fonts/GT-Brush_Script_v2.ttf",
  Caprasimo: "/fonts/Caprasimo-Regular.ttf",
  "Casual Italic": "/fonts/GT-Casual_Italic_Script.ttf",
  "Century Bold Condensed": "/fonts/GT-Century_Bold_Cond.ttf",
  "Century Schoolbook": "/fonts/Domine-SemiBold.ttf",
  "Clarendon Bold": "/fonts/GT-Clarendon_Fortune_Bold_v2.ttf",
  Clarion: "/fonts/GT-Clarion.ttf",
  "Clearview Light": "/fonts/GT-ClearviewOneLight.ttf",
  "Clearview Bold": "/fonts/GT-ClearviewOneBold.ttf",
  Consort: "/fonts/GT-Consort.ttf",
  Consort_Condensed: "/fonts/GT-Consort Condensed.ttf",
  Cookie: "/fonts/Cookie-Regular.ttf",
  "Country Gothic": "/fonts/GT-Country_Gothic.ttf",
  Creepster: "/fonts/Creepster-Regular.ttf",
  Crushed: "/fonts/Crushed-Regular.ttf",
  "Delius Swash Caps": "/fonts/DeliusSwashCaps-Regular.ttf",
  "Exo Bold": "/fonts/Exo2-Bold.ttf",
  Fjalla: "/fonts/FjallaOne-Regular.ttf",
  "Fontdiner Swanky": "/fonts/FontdinerSwanky-Regular.ttf",
  "Friz Quadrata": "/fonts/GT-Friz_Quadrata_v2.ttf",
  Futura: "/fonts/Jost-Medium.ttf",
  "Futura Bold": "/fonts/Jost-Bold.ttf",
  "Futura Condensed": "/fonts/GT-Futura_Condensed_v2.ttf",
  "Futura Demibold": "/fonts/Jost-SemiBold.ttf",
  Gemco: "/fonts/GT-Gemco.ttf",
  "GrandHotel Regular": "/fonts/GrandHotel-Regular.ttf",
  Helvetica: "/fonts/GT-Helvetica_v2.ttf",
  "Helvetica Bold": "/fonts/GT-Helvetica_Bold_v2.ttf",
  "Helvetica Medium Condensed": "/fonts/GT-Helvetica_Medium_Cond_v2.ttf",
  "Herman Italic": "/fonts/GT-Herman_Italic.ttf",
  "Highway Gothic D": "/fonts/HighwayGothicD.otf",
  "Holtwood One": "/fonts/HoltwoodOneSC-Regular.ttf",
  Impact: "/fonts/impact.ttf",
  "Italicized Script": "/fonts/GT-Italicized_Script.ttf",
  Kavoon: "/fonts/Kavoon-Regular.ttf",
  "Lato Bold": "/fonts/Lato-Bold.ttf",
  Leckerli: "/fonts/LeckerliOne-Regular.ttf",
  "Lily Script One": "/fonts/LilyScriptOne-Regular.ttf",
  Limelight: "/fonts/Limelight-Regular.ttf",
  Lobster: "/fonts/Lobster-Regular.ttf",
  "Luckiest Guy": "/fonts/LuckiestGuy-Regular.ttf",
  Magneto: "/fonts/magnetob.ttf",
  "Mystery Quest": "/fonts/MysteryQuest-Regular.ttf",
  "Nerko One": "/fonts/NerkoOne-Regular.ttf",
  Norwester: "/fonts/norwester.otf",
  "Nova Square": "/fonts/NovaSquare-Regular.ttf",
  "Oleo Script Bold": "/fonts/OleoScript-Bold.ttf",
  Optima: "/fonts/GT-Optima_v2.ttf",
  "Optima Semibold": "/fonts/GT-Optima_Semibold_v2.ttf",
  Oswald: "/fonts/Oswald-Regular.ttf",
  Palatino: "/fonts/Merriweather-Regular.ttf",
  "Palatino Semibold": "/fonts/Merriweather-Bold.ttf",
  Peralta: "/fonts/Peralta-Regular.ttf",
  Perfaire: "/fonts/GT-Perfaire.ttf",
  "Permanent Marker": "/fonts/PermanentMarker-Regular.ttf",
  Poetsen: "/fonts/PoetsenOne-Regular.ttf",
  Ranchers: "/fonts/Ranchers-Regular.ttf",
  "Roboto Slab": "/fonts/RobotoSlab-SemiBold.ttf",
  Rubik: "/fonts/Rubik-Regular.ttf",
  "Saira Stencil": "/fonts/SairaStencilOne-Regular.ttf",
  Salsa: "/fonts/Salsa-Regular.ttf",
  Sancreek: "/fonts/Sancreek-Regular.ttf",
  Sarina: "/fonts/Sarina-Regular.ttf",
  Satisfy: "/fonts/Satisfy-Regular.ttf",
  Shojumaru: "/fonts/Shojumaru-Regular.ttf",
  "Short Stack": "/fonts/ShortStack-Regular.ttf",
  Showcard: "/fonts/showg.ttf",
  Sofia: "/fonts/Sofia-Regular.ttf",
  "Standard Block": "/fonts/GT-Standard_Block_v2.ttf",
  "Standard Block Condensed": "/fonts/GT-Standard_Block_Cond.ttf",
  "Times Roman": "/fonts/GT-Times_Roman.ttf",
  "Titillium Web": "/fonts/TitilliumWeb-Regular.ttf",
  VT323: "/fonts/VT323-Regular.ttf",
  "Custom Font": "/fonts/GT-Arial.ttf",
  Roboto: "/fonts/Roboto/Roboto-Bold.ttf",
  Montserrat: "/fonts/Montserrat/Montserrat-Italic-VariableFont_wght.ttf",
};

const CanvasArea = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const isMounted = useRef(false);
  const isPanningRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [isPanMode, setIsPanMode] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 800,
    height: 600,
  });
  const {
    setCanvas,
    setSelectedObject,
    loading,
    setSignCategoryState,
    setAttributes,
    setAttributeOptions,
    setAttributeValues,
    setBasketItem,
    setUpsaleSignPrice,
    signCategorySizeState,
    setSignCategorySizeState,
    setNoticesMessage,
    setSnackbar,
    showRuler,
    setShowRuler,
    setCanvasScale,
    backgroundColor,
    setBackgroundColor,
  } = useData();

  const {
    saveHistory,
    handleUndo,
    handleRedo,
    clearHistory,
    getCurrentHistoryState,
    canUndo,
    canRedo,
  } = useCanvasHistory(fabricCanvasRef, setBackgroundColor);

  const { updateCanvasSize, baseScaleRef } = useCanvasResize(
    fabricCanvasRef,
    containerRef,
    setSnackbar,
    setCanvasScale,
    showRuler,
    saveHistory,
    getCurrentHistoryState,
    setNoticesMessage,
    backgroundColor,
    setCanvasDimensions
  );

  const { zoomLevel, zoomIn, zoomOut } = useZoom(
    fabricCanvasRef,
    baseScaleRef,
    setCanvasScale,
    updateCanvasSize,
    signCategorySizeState.width,
    signCategorySizeState.height,
    setCanvasDimensions
  );

  const handleMouseDown = useCallback(
    (opt) => {
      const initCanvas = fabricCanvasRef.current;
      if (!initCanvas || !isPanMode) return;
      isPanningRef.current = true;
      lastPosRef.current = { x: opt.e.clientX, y: opt.e.clientY };
      initCanvas.selection = false;
      initCanvas.discardActiveObject();
      initCanvas.requestRenderAll();
      console.log("CanvasArea: Panning started", {
        x: lastPosRef.current.x,
        y: lastPosRef.current.y,
        backgroundColor: initCanvas.backgroundColor,
      });
    },
    [isPanMode]
  );

  const handleMouseMove = useCallback((opt) => {
    const initCanvas = fabricCanvasRef.current;
    if (!isPanningRef.current || !initCanvas) return;
    const e = opt.e;
    const vpt = initCanvas.viewportTransform;
    vpt[4] += e.clientX - lastPosRef.current.x;
    vpt[5] += e.clientY - lastPosRef.current.y;
    initCanvas.setViewportTransform(vpt);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    initCanvas.requestRenderAll();
    console.log("CanvasArea: Panning", {
      vpt,
      backgroundColor: initCanvas.backgroundColor,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    const initCanvas = fabricCanvasRef.current;
    if (!initCanvas) return;
    isPanningRef.current = false;
    if (!isPanMode) {
      initCanvas.selection = true;
    }
    initCanvas.requestRenderAll();
    console.log("CanvasArea: Panning stopped", {
      backgroundColor: initCanvas.backgroundColor,
    });
  }, [isPanMode]);

  useEffect(() => {
    console.log("CanvasArea: Fabric.js version", Canvas.version);
  }, []);

  // Preload fonts for Fabric.js
  useEffect(() => {
    console.log("CanvasArea: Preloading fonts for Fabric.js", FontNames);
    FontNames.forEach((font) => {
      const fontUrl = FONT_PATHS[font];
      if (!fontUrl) {
        console.warn(`CanvasArea: No font URL defined for ${font}`);
        return;
      }
      try {
        const fontFormat = fontUrl.endsWith(".otf") ? "opentype" : "truetype";
        const fontFace = new FontFace(font, `url(${fontUrl})`, {
          format: fontFormat,
        });
        document.fonts.add(fontFace);
        fontFace
          .load()
          .then(() =>
            console.log(`CanvasArea: Font loaded for Fabric.js: ${font}`)
          )
          .catch((err) =>
            console.warn(
              `CanvasArea: Font ${font} failed to load for Fabric.js:`,
              err
            )
          );
      } catch (error) {
        console.error(
          `CanvasArea: Failed to create FontFace for ${font}:`,
          error
        );
      }
    });
  }, []);

  useEffect(() => {
    isMounted.current = true;
    console.log("CanvasArea: Mounting", {
      container: !!containerRef.current,
      canvasEl: !!canvasRef.current,
      signCategorySizeState,
      backgroundColor,
    });

    if (!containerRef.current || !canvasRef.current) {
      console.warn("CanvasArea: Missing refs", {
        container: !!containerRef.current,
        canvasEl: !!canvasRef.current,
      });
      return;
    }

    if (fabricCanvasRef.current) {
      console.log("CanvasArea: Disposing existing canvas");
      fabricCanvasRef.current.dispose();
      fabricCanvasRef.current = null;
    }

    console.log("CanvasArea: Checking fabric import", {
      Canvas: Canvas,
    });
    if (!Canvas) {
      console.error("CanvasArea: Canvas is not defined");
      setSnackbar({
        open: true,
        message: "Failed to initialize canvas. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      console.log("CanvasArea: Initializing Fabric.js");
      const initCanvas = new Canvas(canvasRef.current, {
        width: 800,
        height: 600,
        backgroundColor: backgroundColor || "#ffffff",
        preserveObjectStacking: true,
        fireRightClick: true,
      });

      fabricCanvasRef.current = initCanvas;
      setCanvas(initCanvas);
      console.log("CanvasArea: Canvas initialized with background color", {
        backgroundColor: initCanvas.backgroundColor,
        canvasWidth: initCanvas.getWidth(),
        canvasHeight: initCanvas.getHeight(),
        viewportTransform: initCanvas.viewportTransform,
        zoom: initCanvas.getZoom(),
        objects: initCanvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
      });

      // Set properties for useCanvasHistory
      initCanvas.__isMounted = isMounted.current;
      initCanvas.__setSelectedObject = setSelectedObject;

      // Event handlers
      initCanvas.on("object:added", () => {
        console.log("CanvasArea: Object added, saving history");
        saveHistory(initCanvas);
      });
      initCanvas.on("object:modified", () => {
        console.log("CanvasArea: Object modified, saving history");
        saveHistory(initCanvas);
      });
      initCanvas.on("object:removed", () => {
        console.log("CanvasArea: Object removed, saving history");
        saveHistory(initCanvas);
      });
      initCanvas.on("selection:created", (e) => {
        if (isMounted.current) {
          setSelectedObject(e.selected?.[0] || null);
          console.log("CanvasArea: Selection created", {
            target: e.selected?.[0]
              ? { type: e.selected[0].type, text: e.selected[0].text }
              : null,
            backgroundColor: initCanvas.backgroundColor,
          });
        }
      });
      initCanvas.on("selection:updated", (e) => {
        if (isMounted.current) {
          setSelectedObject(e.selected?.[0] || null);
          console.log("CanvasArea: Selection updated", {
            target: e.selected?.[0]
              ? { type: e.selected[0].type, text: e.selected[0].text }
              : null,
            backgroundColor: initCanvas.backgroundColor,
          });
        }
      });
      initCanvas.on("selection:cleared", () => {
        if (isMounted.current) {
          setSelectedObject(null);
          console.log("CanvasArea: Selection cleared", {
            backgroundColor: initCanvas.backgroundColor,
          });
        }
      });

      initCanvas.on("mouse:down", handleMouseDown);
      initCanvas.on("mouse:move", handleMouseMove);
      initCanvas.on("mouse:up", handleMouseUp);

      initCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      initCanvas.setZoom(1);

      if (signCategorySizeState.width && signCategorySizeState.height) {
        updateCanvasSize(
          signCategorySizeState.width,
          signCategorySizeState.height,
          initCanvas,
          zoomLevel
        );
      }
    } catch (error) {
      console.error("CanvasArea: Failed to initialize Canvas", error);
      setSnackbar({
        open: true,
        message: "Error initializing canvas: " + error.message,
        severity: "error",
      });
    }

    return () => {
      isMounted.current = false;
      console.log("CanvasArea: Cleanup", {
        objects: fabricCanvasRef.current
          ? fabricCanvasRef.current.getObjects()
          : [],
        backgroundColor: fabricCanvasRef.current?.backgroundColor,
      });
      // Do not dispose of the canvas immediately; handle disposal in a controlled way
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setCanvas(null);
    };
  }, [
    setCanvas,
    setSelectedObject,
    setSnackbar,
    isPanMode,
    signCategorySizeState,
    zoomLevel,
    updateCanvasSize,
    backgroundColor,
    saveHistory,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  // Sync backgroundColor changes
  useEffect(() => {
    const initCanvas = fabricCanvasRef.current;
    if (initCanvas && backgroundColor) {
      try {
        initCanvas.backgroundColor = backgroundColor;
        initCanvas.requestRenderAll();
        console.log("CanvasArea: Updated background color", {
          backgroundColor,
          canvasWidth: initCanvas.getWidth(),
          canvasHeight: initCanvas.getHeight(),
          viewportTransform: initCanvas.viewportTransform,
          zoom: initCanvas.getZoom(),
          objects: initCanvas.getObjects().map((obj) => ({
            type: obj.type,
            top: obj.top,
            left: obj.left,
          })),
        });
      } catch (error) {
        console.error("CanvasArea: Failed to update background color", {
          error: error.message,
          backgroundColor,
        });
        setSnackbar({
          open: true,
          message: "Error updating background color: " + error.message,
          severity: "error",
        });
      }
    }
  }, [backgroundColor, setSnackbar]);

  useEffect(() => {
    const initCanvas = fabricCanvasRef.current;
    if (
      !canvasRef.current ||
      !initCanvas ||
      !signCategorySizeState.width ||
      !signCategorySizeState.height
    ) {
      console.log("CanvasArea: Skipping resize due to missing dependencies", {
        canvasEl: !!canvasRef.current,
        fabricCanvas: !!initCanvas,
        width: signCategorySizeState.width,
        height: signCategorySizeState.height,
      });
      return;
    }

    if (!initCanvas.lowerCanvasEl?.getContext) {
      console.warn("CanvasArea: Cannot resize, canvas not ready");
      return;
    }

    updateCanvasSize(
      signCategorySizeState.width,
      signCategorySizeState.height,
      initCanvas,
      zoomLevel
    );
  }, [
    signCategorySizeState,
    showRuler,
    updateCanvasSize,
    zoomLevel,
    saveHistory,
  ]);

  const handleToggleRuler = useCallback(() => {
    console.log("CanvasArea: Before toggle ruler", {
      showRuler,
      objects: fabricCanvasRef.current?.getObjects() || [],
      backgroundColor: fabricCanvasRef.current?.backgroundColor,
    });
    if (fabricCanvasRef.current) {
      saveHistory(fabricCanvasRef.current);
      setShowRuler((prev) => {
        const newShowRuler = !prev;
        updateCanvasSize(
          signCategorySizeState.width,
          signCategorySizeState.height,
          fabricCanvasRef.current,
          zoomLevel
        );
        console.log("CanvasArea: Ruler toggled", {
          showRuler: newShowRuler,
          backgroundColor: fabricCanvasRef.current.backgroundColor,
        });
        return newShowRuler;
      });
    }
  }, [
    showRuler,
    saveHistory,
    setShowRuler,
    updateCanvasSize,
    signCategorySizeState,
    zoomLevel,
  ]);

  const handleTogglePan = useCallback(() => {
    setIsPanMode((prev) => {
      const newPanMode = !prev;
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.selection = !newPanMode;
        if (newPanMode) {
          fabricCanvasRef.current.discardActiveObject();
        }
      }
      console.log("CanvasArea: Pan mode toggled", {
        isPanMode: newPanMode,
        backgroundColor: fabricCanvasRef.current?.backgroundColor,
      });
      return newPanMode;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (fabricCanvasRef.current) {
      console.log("CanvasArea: Resetting canvas and clearing history");
      fabricCanvasRef.current.clear();
      clearHistory();
      fabricCanvasRef.current.setViewportTransform([1, 0, 0, 1, 0, 0]);
      fabricCanvasRef.current.setZoom(1);
      fabricCanvasRef.current.backgroundColor = "#ffffff";
      fabricCanvasRef.current.requestRenderAll();
      fabricCanvasRef.current.selection = true;
      setBackgroundColor("#ffffff");
      setCanvasDimensions({ width: 800, height: 600 });
      console.log("CanvasArea: Canvas reset with background color", {
        backgroundColor: "#ffffff",
        canvasWidth: fabricCanvasRef.current.getWidth(),
        canvasHeight: fabricCanvasRef.current.getHeight(),
        viewportTransform: fabricCanvasRef.current.viewportTransform,
        zoom: fabricCanvasRef.current.getZoom(),
        objects: fabricCanvasRef.current.getObjects(),
      });
    }
    setIsPanMode(false);
    fabricCanvasRef.current.selection = true;
    setSignCategoryState({ name: "" });
    setAttributes({});
    setAttributeOptions({});
    setAttributeValues({});
    setBasketItem({});
    setUpsaleSignPrice("0.00");
    setSignCategorySizeState({ width: 0, height: 0 });
    setNoticesMessage("");
    setSnackbar({
      open: true,
      message: "Canvas reset successfully.",
      severity: "info",
    });
  }, [
    setSignCategoryState,
    setAttributes,
    setAttributeOptions,
    setAttributeValues,
    setBasketItem,
    setUpsaleSignPrice,
    setSignCategorySizeState,
    setNoticesMessage,
    setSnackbar,
    clearHistory,
    setBackgroundColor,
  ]);

  const handleOpenResetDialog = () => {
    setOpenResetDialog(true);
  };

  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
  };

  const handleConfirmReset = () => {
    handleReset();
    setOpenResetDialog(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        minHeight: "200px",
        maxHeight: "calc(100vh - 170px - 32px)",
        paddingTop: 4,
        paddingBottom: 2,
        paddingX: 4,
        margin: 0,
        marginLeft: 4,
        marginRight: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontFamily: "Roboto, sans-serif",
        zIndex: 900,
        overflow: "visible",
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={handleToggleRuler}>
          {showRuler ? "Hide Ruler" : "Show Ruler"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<UndoIcon />}
          onClick={handleUndo}
          disabled={!canUndo}
        >
          Undo
        </Button>
        <Button
          variant="outlined"
          startIcon={<RedoIcon />}
          onClick={handleRedo}
          disabled={!canRedo}
        >
          Redo
        </Button>
        <Button
          variant="outlined"
          startIcon={<ZoomInIcon />}
          onClick={zoomIn}
          disabled={zoomLevel >= 5.0}
        >
          Zoom In
        </Button>
        <Button
          variant="outlined"
          startIcon={<ZoomOutIcon />}
          onClick={zoomOut}
          disabled={zoomLevel <= 0.1}
        >
          Zoom Out
        </Button>
        <Button
          variant="outlined"
          startIcon={<PanToolIcon />}
          onClick={handleTogglePan}
        >
          {isPanMode ? "Disable Pan" : "Enable Pan"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleOpenResetDialog}
          disabled={loading}
        >
          Reset Design
        </Button>
      </Stack>
      <Dialog
        open={openResetDialog}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">Confirm Reset</DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            All the objects in your canvas will be removed and its history
            deleted. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        ref={containerRef}
        id="canvasContainer"
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "visible",
        }}
      >
        <Ruler
          width={signCategorySizeState.width}
          height={signCategorySizeState.height}
          showRuler={showRuler}
          baseScale={baseScaleRef.current}
        >
          <Box sx={{ position: "relative" }}>
            <canvas
              ref={canvasRef}
              id="canvas"
              style={{
                border: "1px solid gray",
                display: "block",
                width: `${canvasDimensions.width}px`,
                height: `${canvasDimensions.height}px`,
              }}
            />
          </Box>
        </Ruler>
      </Box>
    </Box>
  );
};

export default CanvasArea;
